import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, MailCheck } from "lucide-react";
import { useAuth, API, formatApiErrorDetail } from "@/context/AuthContext";

const EASE = [0.16, 1, 0.3, 1];

export default function AuthPage() {
  const [tab, setTab] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const url = tab === "login" ? "/auth/login" : "/auth/register";
      const payload =
        tab === "login"
          ? { email: form.email, password: form.password }
          : { prenom: form.prenom, nom: form.nom, email: form.email, password: form.password };
      const { data } = await axios.post(`${API}${url}`, payload, { withCredentials: true });
      setUser(data);
      navigate("/espace");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: form.email });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/espace";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const titles = { login: "Connexion", register: "Créer un compte", forgot: "Mot de passe oublié" };

  return (
    <div className="info-page" data-testid="auth-page">
      <header className="info-header">
        <Link to="/" className="info-back" data-testid="back-home-link">
          <ArrowLeft size={16} />
          Retour à l'accueil
        </Link>
        <Link to="/" className="logo logo-link" data-testid="brand-home-link" aria-label="SamNara — accueil"><img src="/logo-samnara.png" alt="SamNara" className="logo-img logo-img-lg" /></Link>
      </header>

      <div className="container auth-wrap">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE }}
        >
          <div className="eyebrow checkout-eyebrow">Espace membre</div>
          <h1 className="checkout-title">{titles[tab]}</h1>

          <div className="checkout-panel">
            {tab !== "forgot" && (
              <div className="auth-tabs" data-testid="auth-tabs">
                <button
                  className={tab === "login" ? "active" : ""}
                  onClick={() => { setTab("login"); setError(""); }}
                  data-testid="tab-login"
                >
                  Connexion
                </button>
                <button
                  className={tab === "register" ? "active" : ""}
                  onClick={() => { setTab("register"); setError(""); }}
                  data-testid="tab-register"
                >
                  Créer un compte
                </button>
              </div>
            )}

            {tab === "forgot" ? (
              forgotSent ? (
                <div className="forgot-sent" data-testid="forgot-success">
                  <MailCheck size={26} />
                  <b>Email envoyé !</b>
                  <p>Si un compte existe avec cet email, un lien de réinitialisation vient d'être envoyé. Vérifie ta boîte (et les spams) — le lien est valable 1 heure.</p>
                  <button className="auth-link" onClick={() => { setTab("login"); setForgotSent(false); }} data-testid="forgot-back-login">
                    Retour à la connexion
                  </button>
                </div>
              ) : (
                <form onSubmit={submitForgot} className="auth-form">
                  <p className="forgot-intro">Entre l'email de ton compte : tu recevras un lien pour choisir un nouveau mot de passe.</p>
                  {error && <div className="auth-error" data-testid="auth-error">{error}</div>}
                  <input
                    type="email"
                    placeholder="Ton email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    data-testid="forgot-email-input"
                  />
                  <button type="submit" className="btn cf-submit" disabled={loading} data-testid="forgot-submit-btn">
                    {loading ? "Envoi…" : "Envoyer le lien"}
                  </button>
                  <button type="button" className="auth-link" onClick={() => { setTab("login"); setError(""); }} data-testid="forgot-back-login">
                    Retour à la connexion
                  </button>
                </form>
              )
            ) : (
              <>
                <button className="btn google-btn" onClick={googleLogin} data-testid="google-login-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.02c2.2-2 3.5-5 3.5-8.6z"/><path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5l-.14.01-3.1 2.4-.04.13C4 21.3 7.7 24 12 24z"/><path fill="#FBBC05" d="M5.3 14.4c-.2-.7-.4-1.5-.4-2.4s.2-1.7.4-2.4l-.01-.16-3.15-2.4-.1.05C1.4 8.7 1 10.3 1 12s.4 3.3 1.1 4.9l3.2-2.5z"/><path fill="#EA4335" d="M12 4.6c2.3 0 3.8 1 4.7 1.8l3.4-3.3C18 1.2 15.2 0 12 0 7.7 0 4 2.7 2.1 6.7l3.2 2.5c.9-2.9 3.6-4.6 6.7-4.6z"/></svg>
                  Continuer avec Google
                </button>

                <div className="auth-sep"><span>ou par email</span></div>

                {error && <div className="auth-error" data-testid="auth-error">{error}</div>}

                <form onSubmit={submit} className="auth-form">
                  {tab === "register" && (
                    <div className="cf-row">
                      <input
                        type="text"
                        placeholder="Prénom"
                        value={form.prenom}
                        onChange={update("prenom")}
                        required
                        data-testid="register-prenom-input"
                      />
                      <input
                        type="text"
                        placeholder="Nom"
                        value={form.nom}
                        onChange={update("nom")}
                        required
                        data-testid="register-nom-input"
                      />
                    </div>
                  )}
                  <input
                    type="email"
                    placeholder="Ton email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    data-testid="auth-email-input"
                  />
                  <input
                    type="password"
                    placeholder="Mot de passe (8 caractères min.)"
                    value={form.password}
                    onChange={update("password")}
                    required
                    minLength={8}
                    data-testid="auth-password-input"
                  />
                  {tab === "login" && (
                    <button type="button" className="auth-link" onClick={() => { setTab("forgot"); setError(""); }} data-testid="forgot-link">
                      Mot de passe oublié ?
                    </button>
                  )}
                  <button type="submit" className="btn cf-submit" disabled={loading} data-testid="auth-submit-btn">
                    {loading ? "Patiente…" : tab === "login" ? "Se connecter" : "Créer mon compte"}
                  </button>
                </form>

                <p className="auth-hint">
                  Tes ebooks achetés sur la boutique se débloquent dans ton espace avec le code d'activation reçu après l'achat.
                </p>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
